/**
 * passkeyLoginErrors — the machine codes the discoverable passkey LOGIN ceremony throws for the
 * failures that happen in the BROWSER, before or instead of a server round-trip.
 *
 * USER-FACING-GEMS L1 (2026-09-29). A server failure already arrives in the fleet error
 * vocabulary (`status` / `errorCode` from `@puffin-core/auth-client`); these two cannot, because
 * no request was made. Giving them a code — never a sentence to substring-match — lets every
 * consumer classify them with one lookup: `usePasskeyLogin` maps them to login-namespace copy, and
 * pos-web's `authErrorLeaf` maps the same values to its own catalog.
 *
 * A ZERO-IMPORT leaf, so a suite that doubles the ceremony cannot turn the vocabulary into
 * `undefined` at module evaluation (the reason pos-web's `authErrorCodes.js` owns its codes).
 *
 * @module @puffin-core/login-shell/utils/passkeyLoginErrors
 */

/**
 * @constant {Readonly<{WEBAUTHN_UNSUPPORTED: string, WEBAUTHN_CANCELLED: string}>}
 *   - `WEBAUTHN_UNSUPPORTED` — the browser has no WebAuthn implementation.
 *   - `WEBAUTHN_CANCELLED` — the user dismissed the authenticator prompt (or it timed out).
 */
export const PASSKEY_LOGIN_ERROR_CODES = Object.freeze({
  WEBAUTHN_UNSUPPORTED: 'WEBAUTHN_UNSUPPORTED',
  WEBAUTHN_CANCELLED: 'WEBAUTHN_CANCELLED',
});

/**
 * The SERVER's code when the passkey challenge store cannot answer (`@puffin-core/passkey`
 * `PASSKEY_CHALLENGE_STORE_UNAVAILABLE`, a 503; ECOSYSTEM-EMAIL-001 Q-EE-41). It arrives as the failure's `errorCode`,
 * and is restated here because this zero-import leaf depends on no API package.
 */
export const PASSKEY_CHALLENGE_STORE_UNAVAILABLE_CODE = 'PASSKEY_CHALLENGE_STORE_UNAVAILABLE';

/**
 * Build an `Error` carrying a machine `code`. The message is for logs only; nothing renders it.
 *
 * @param {string} code - One of `PASSKEY_LOGIN_ERROR_CODES`.
 * @param {string} devMessage - Developer-facing message.
 * @param {unknown} [cause] - The underlying browser error, when there was one.
 * @returns {Error & {code: string}}
 */
export function passkeyLoginError(code, devMessage, cause) {
  const err = new Error(devMessage);
  err.code = code;
  if (cause !== undefined) err.cause = cause;
  return err;
}
